import {
    Button, ButtonGroup, IconButton, Menu, MenuButton, MenuItem, MenuList, Tooltip,
} from '@chakra-ui/react';
import { useExperiment } from '@acp/feature/auth';
import { format } from 'date-fns';
import { FaIcon, ThemeTokens } from '@acp/core-ui';
import { useQueryClient } from '@tanstack/react-query';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { Experiment } from '@acp/shared';
import { useUnderEnrolmentNewManagement } from '../hooks/use-under-enrolment-new-management';
import { useDismissNewManagement } from '../hooks/use-dismiss-new-management';
import { NewWorkOrderButton } from '../../property-request';

export function NewManagementEnrolButton({
    isUnderEnrolment: _isUnderEnrolment,
    underEnrolmentBy,
    underEnrolmentAt,
    portfolioPropertyId,
    address,
    ...props
}: {
    isUnderEnrolment: boolean;
    underEnrolmentBy: string | null;
    underEnrolmentAt: string | null;
    portfolioPropertyId: number;
    address: string;
} & Omit<React.ComponentProps<typeof Button>, 'children'>) {
    const queryClient = useQueryClient();

    const underEnrolmentMutation = useUnderEnrolmentNewManagement();
    const dismissNewManagementMutation = useDismissNewManagement({
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: ['dashboard', 'tab', 'new-managements'],
            });
        },
    });

    const { hasExperiment } = useExperiment();

    const hasPropertyRequestExperiment = hasExperiment(Experiment.PropertyRequestEnrolmentV1);
    const isUnderEnrolment = _isUnderEnrolment && !hasPropertyRequestExperiment;

    const handleNewWorkOrderButtonClick = () => {
        if (hasPropertyRequestExperiment) return;

        underEnrolmentMutation.markAsUnderEnrolment({
            agencyPortfolioPropertyId: portfolioPropertyId,
        });
    };

    return (
        <ButtonGroup size="sm" isAttached colorScheme="charcoal" variant="outline" justifySelf="end">
            { isUnderEnrolment && (
                <Tooltip label={`Enrolment started by ${underEnrolmentBy ?? 'unknown'} ${underEnrolmentAt !== null ? `on ${format(underEnrolmentAt, 'PPP')}` : ''}`} bgColor="white" color="black">
                    <Button
                        textDecoration="none"
                        colorScheme="charcoal"
                        variant="outline"
                        size="sm"
                        color="muted.700"
                        borderColor={ThemeTokens.Defaults.borderColor}
                        isDisabled
                    >
                        Enrolment in progress
                    </Button>
                </Tooltip>
            )}
            { !isUnderEnrolment && (
                <NewWorkOrderButton
                    borderColor={ThemeTokens.Defaults.borderColor}
                    onClick={handleNewWorkOrderButtonClick}
                    legacyPath={`/JobOrders/add?address=${address}&agencyPortfolioPropertyId=${portfolioPropertyId}`}
                    eventGenerator={() => ({
                        type: 'OPEN',
                        prefilledAddress: address,
                        excludeEnrolledProperties: true,
                    })}
                    {...props}
                >
                    Enrol property
                </NewWorkOrderButton>
            )}
            <Menu size="large">
                <MenuButton
                    as={IconButton}
                    borderStartRadius={0}
                    aria-label="Add to friends"
                    icon={<FaIcon icon={faChevronDown} />}
                    borderColor={ThemeTokens.Defaults.borderColor}
                    color="muted.700"
                />
                <MenuList>
                    <MenuItem
                        onClick={() => dismissNewManagementMutation
                            .dismissNewManagement({
                                agencyPortfolioPropertyId: portfolioPropertyId,
                            })}
                    >
                        Dismiss
                    </MenuItem>
                </MenuList>
            </Menu>
        </ButtonGroup>
    );
}
